//---------------------------------------------------------
// Soft #40: dashboard cards driven by the OAM PromQL proxy.
// Every card names one allowlisted chart; the server holds the query.
//---------------------------------------------------------
import {Box, LinearProgress, Skeleton, Stack, Typography} from '@mui/material';
import AnimatedValue from 'components/element/AnimatedValue';
import PieChartWithTitle from 'components/element/PieChartWithTitle';
import RateLineGraph from 'components/element/RateLineGraph';
import {useMetricsChart, useScalarHistory} from 'hooks/query/metricsChartsHook';
import {t} from 'i18next';
import {ReactNode} from 'react';
import {IMetricsChartResult, MetricsChartError, MetricsChartName} from 'types/metricsCharts';
import {IInstance} from 'types/oam';
import CardBase from './CardBase';
import TileScrollBody, {TILE_ROW} from './TileScrollBody';
import {hasSamples, IBarRow, sumSeries, toBackendRows, toClientRows, toServicePie} from './promChartData';

//---------------------------------------------------------
// Shared states
//---------------------------------------------------------
export function chartErrorText(error: unknown): string {
	if (error instanceof MetricsChartError && error.code === 'METRICS_NOT_CONFIGURED') return t("Chart data isn't set up on this console yet.");
	if (error instanceof MetricsChartError && error.code === 'BACKENDS_UNAVAILABLE') return t("The registered backend list can't be read right now.");
	if (error instanceof MetricsChartError && error.code === 'NOT_FOUND') return t("This instance isn't registered in the console.");
	return t('Chart data is unavailable right now.');
}

export function NotAvailable(props: {detail?: string}) {
	return (
		<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" minHeight={80} gap={0.5} data-testid="chart-not-available">
			<Typography variant="body2" color="text.secondary">
				{t('Not available')}
			</Typography>
			{props.detail && (
				<Typography variant="caption" color="text.secondary" textAlign="center">
					{props.detail}
				</Typography>
			)}
		</Box>
	);
}

export interface IChartCardProps {
	instance: IInstance | null;
}

// No instance → Not available; loading → skeleton; error → inline message
// (never the global error page); no series → Not available; otherwise render(data).
function ChartFrame(props: {title: string; name: MetricsChartName; instance: IInstance | null; seriesKey?: string; emptyDetail?: string; children: (data: IMetricsChartResult) => ReactNode}) {
	const {title, name, instance, seriesKey, emptyDetail, children} = props;
	const {data, error, isLoading} = useMetricsChart(name, instance?.id);
	let body: ReactNode;
	if (!instance) body = <NotAvailable detail={t('Select an instance to see this chart.')} />;
	else if (isLoading) body = <Skeleton variant="rounded" width="100%" height={80} />;
	else if (error) body = <NotAvailable detail={chartErrorText(error)} />;
	else if (!data || !hasSamples(data, seriesKey)) body = <NotAvailable detail={emptyDetail} />;
	else body = children(data);
	return <CardBase title={title}>{body}</CardBase>;
}

function formatPerSecond(v: number): string {
	if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M/s`;
	if (v >= 1e3) return `${(v / 1e3).toFixed(2)}K/s`;
	return `${v.toFixed(v < 10 ? 2 : 1)}/s`;
}

function BarList(props: {label: string; rows: IBarRow[]; format: (r: IBarRow) => string; truncated?: boolean}) {
	return (
		<TileScrollBody label={props.label}>
		<Stack gap={1} sx={{pr: 0.5}}>
			{props.rows.map((r, i) => (
				<Box key={`${r.label}-${r.sublabel ?? ''}-${i}`} {...TILE_ROW}>
					<Box display="flex" justifyContent="space-between" gap={1}>
						<Typography variant="caption" sx={{fontFamily: 'monospace'}} noWrap title={r.label}>
							{r.label}
							{r.sublabel ? (
								<Typography component="span" variant="caption" color="text.secondary">
									{`  ${r.sublabel}`}
								</Typography>
							) : null}
						</Typography>
						<Typography variant="caption" sx={{fontVariantNumeric: 'tabular-nums'}}>
							{props.format(r)}
						</Typography>
					</Box>
					<LinearProgress variant="determinate" value={r.percent} sx={{height: 6, borderRadius: 3, backgroundColor: 'grey.200'}} />
				</Box>
			))}
			{props.truncated && (
				<Typography variant="caption" color="text.secondary" {...TILE_ROW}>
					{t('Showing the largest {{count}}.', {count: props.rows.length})}
				</Typography>
			)}
		</Stack>
		</TileScrollBody>
	);
}

//---------------------------------------------------------
// Total Requests (rate row)
//---------------------------------------------------------
export function PromTotalRequestsCard({instance}: IChartCardProps) {
	const title = t('Total Requests');
	const {data} = useMetricsChart('total-requests', instance?.id);
	const history = useScalarHistory(data, r => sumSeries(r, 'rate'));
	return (
		<ChartFrame title={title} name="total-requests" instance={instance} seriesKey="rate">
			{r => {
				const current = sumSeries(r, 'rate') ?? 0;
				return (
					<Box display="flex" flexDirection="column" gap={1}>
						<Box display="flex" justifyContent="space-between" alignItems="center">
							<Typography variant="caption" color="textSecondary">
								{t('Requests per second, 5 minute rate')}
							</Typography>
							<AnimatedValue variant="h6" sx={{fontVariantNumeric: 'tabular-nums'}} value={formatPerSecond(current)} />
						</Box>
						{history.length > 1 && <RateLineGraph data={{label: title, values: history}} unit="count" />}
					</Box>
				);
			}}
		</ChartFrame>
	);
}

//---------------------------------------------------------
// Traffic Distribution by Service
//---------------------------------------------------------
export function PromTrafficDistributionCard({instance}: IChartCardProps) {
	return (
		<ChartFrame title={t('Traffic Distribution by Service')} name="traffic-distribution" instance={instance} seriesKey="share">
			{r => {
				const slices = toServicePie(r, t('Unnamed service'));
				if (slices.length === 0) return <NotAvailable />;
				return <PieChartWithTitle data={slices} />;
			}}
		</ChartFrame>
	);
}

//---------------------------------------------------------
// Backend balance (registered backends only)
//---------------------------------------------------------
export function PromBackendBalanceCard({instance}: IChartCardProps) {
	return (
		<ChartFrame title={t('Backend balance')} name="backend-balance" instance={instance} seriesKey="share" emptyDetail={t('No registered backends on this instance yet.')}>
			{r => (
				<Box display="flex" flexDirection="column" gap={1} height="100%" minHeight={0}>
					<Typography variant="caption" color="text.secondary">
						{t('Registered backend, service, share of service traffic')}
					</Typography>
					<BarList label={t('Backend balance')} rows={toBackendRows(r, t('Unnamed service'))} truncated={r.series[0]?.truncated} format={row => `${(row.value * 100).toFixed(1)}%`} />
				</Box>
			)}
		</ChartFrame>
	);
}

//---------------------------------------------------------
// Top Clients
//---------------------------------------------------------
export function PromTopClientsCard({instance}: IChartCardProps) {
	return (
		<ChartFrame title={t('Top Clients')} name="top-clients" instance={instance} seriesKey="pps" emptyDetail={t("This instance doesn't export per-client traffic yet.")}>
			{r => (
				<Box display="flex" flexDirection="column" gap={1} height="100%" minHeight={0}>
					<Typography variant="caption" color="text.secondary">
						{t('Client address, packets per second, 5 minute rate')}
					</Typography>
					<BarList label={t('Top Clients')} rows={toClientRows(r)} format={row => formatPerSecond(row.value)} />
				</Box>
			)}
		</ChartFrame>
	);
}
